import { SnackbarContext } from '@/contexts/SnackbarContext';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import Input from '@superfunnel/ui-core/v2/Input';
import { useCallback, useContext, useEffect, useState } from 'react';
import { UserContext } from '../contexts/UserContext';
import { createAdminCheckoutSession } from '../services/BillingService';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function CustomCheckoutModal({ open, onClose }: Props) {
  const { show } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);

  const [variantId, setVariantId] = useState('');
  const [discountCode, setDiscountCode] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    if (!open) return;

    setVariantId('');
    setDiscountCode('');
    setSaving(false);
    setError(undefined);
  }, [open]);

  const handleCreateCheckout = useCallback(async () => {
    if (saving) return;
    if (!selectedAccount) return;

    if (!variantId || variantId.trim().length === 0) {
      setError(new Error('Variant ID is required'));
      return;
    }

    setSaving(true);
    setError(undefined);

    try {
      const response = await createAdminCheckoutSession(
        selectedAccount.id,
        variantId.trim(),
        window.location.href,
        discountCode.trim() || undefined,
      );

      if (response.checkoutUrl) {
        onClose();
        // copy to clipboard
        window.navigator.clipboard.writeText(response.checkoutUrl);
        show('Checkout URL copied to clipboard', 'success');
      } else {
        setError(new Error('No checkout URL returned'));
      }
    } catch (err) {
      console.error('Failed to create admin checkout:', err);
      setError(
        err instanceof Error ? err : new Error('Failed to create checkout'),
      );
    } finally {
      setSaving(false);
    }
  }, [saving, selectedAccount, variantId, discountCode, onClose, show]);

  const actions: ModalActionData[] = [
    {
      type: 'secondary',
      title: 'Cancel',
      onClick: onClose,
      disabled: saving,
    },
    {
      type: 'primary',
      title: saving ? 'Creating...' : 'Create Checkout',
      onClick: handleCreateCheckout,
      loading: saving,
      disabled: saving || !variantId,
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Custom Checkout (Admin)"
      description="Create a checkout session for any Lemon Squeezy product and variant"
      actions={actions}
      width="md"
    >
      <div className="flex flex-col gap-4">
        <Input
          title="Variant ID"
          value={variantId}
          onChange={(e) => setVariantId(e.target.value)}
          placeholder="Enter Lemon Squeezy Variant ID"
          disabled={saving}
        />

        <Input
          title="Discount Code (optional)"
          value={discountCode}
          onChange={(e) => setDiscountCode(e.target.value)}
          placeholder="Enter discount code"
          disabled={saving}
        />

        {error && <p className="text-sm text-red-500">{error.message}</p>}
      </div>
    </Modal>
  );
}
